import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useSetAtom } from 'jotai';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { getPlainDateString } from '../../../services/queries/MaggieMetricsQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import {
  AtlasUploadOpts,
  getCartaGlFileStatuses,
  getCartaGlPresignedUrls,
  uploadAtlasGeneralLedger,
} from '../../../services/queries/MaggieTransactionQueries';
import { uploadToS3 } from '../../../services/queries/SoiAgentQueries';
import { ROUTES } from '../../../constants/RouteNameMapping';

import { invalidateGeneralLedgerData } from './GeneralLedger';
import { FileUploadEntry, FileUploadStatus } from './multiFileUploadState';

const CARTA_GL_POLL_INTERVAL_MS = 2000;
const CARTA_GL_POLL_TIMEOUT_MS = 5 * 60 * 1000;
const CARTA_GL_STILL_PROCESSING = 'Still processing — check the Finance module shortly.';

export type CartaGlPollResult =
  { status: 'completed' } | { status: 'error'; errorMessage: string } | { status: 'timeout' };

/** Poll the Carta GL status endpoint for one cartaGlFile row until it reaches a terminal
 *  state. The presigned upload only *accepts* the file (async SQS ingest follows), so the
 *  operator's true outcome comes from this poll, not the S3 PUT. */
export async function pollCartaGlCompletion(
  cartaGlFileId: number,
  opts: { intervalMs?: number; timeoutMs?: number } = {}
): Promise<CartaGlPollResult> {
  const intervalMs = opts.intervalMs ?? CARTA_GL_POLL_INTERVAL_MS;
  const timeoutMs = opts.timeoutMs ?? CARTA_GL_POLL_TIMEOUT_MS;
  const deadline = Date.now() + timeoutMs;

  for (;;) {
    try {
      const rows = await getCartaGlFileStatuses();
      const row = rows.find((r) => r.id === cartaGlFileId);
      if (row?.status === 'completed') {
        return { status: 'completed' };
      }
      if (row?.status === 'error') {
        return { status: 'error', errorMessage: row.errorMessage ?? 'Processing failed' };
      }
    } catch (error) {
      // A transient status-endpoint failure must not fail the upload — the file may still
      // be processing server-side. Swallow it and keep polling until the deadline.
      console.error('(pollCartaGlCompletion) status poll failed, will retry', error);
    }
    if (Date.now() >= deadline) {
      return { status: 'timeout' };
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

/** Presign → PUT to S3 → poll to completion for a single Carta GL file. Throws only on
 *  presign/PUT failure; a processing failure is returned as a resolved error result so the
 *  caller can attribute it to the right row. */
async function uploadAndAwaitCartaGl(file: File, period: string): Promise<CartaGlPollResult> {
  const [upload] = await getCartaGlPresignedUrls([{ fileName: file.name, period }]);
  if (!upload) {
    throw new Error('Presign returned no upload slot for this file.');
  }
  await uploadToS3(upload.signedUrl, file);
  return pollCartaGlCompletion(upload.cartaGlFileId);
}

export function useUploadGeneralLedger() {
  const { pushInfoToast, pushErrorToast } = useToastMessageState();
  const navigate = useNavigate();
  const setInvalidator = useSetAtom(invalidateGeneralLedgerData);

  return useCallback(
    async (file: File, date: Date) => {
      try {
        const result = await uploadAndAwaitCartaGl(file, getPlainDateString(date));
        setInvalidator((prev) => prev + 1);
        if (result.status === 'completed') {
          pushInfoToast({
            message: 'Upload processed. You can review transactions in Finance module',
            buttonLabel: 'Go to Finance',
            buttonAction: () => {
              navigate(`/${ROUTES.FINANCE2}/${ROUTES.FINANCE2_OVERVIEW}`);
            },
          });
        } else if (result.status === 'error') {
          pushErrorToast({ message: `General Ledger upload failed: ${result.errorMessage}` });
        } else {
          pushErrorToast({ message: CARTA_GL_STILL_PROCESSING });
        }
      } catch (error) {
        console.error(error);
        pushErrorToast({ message: getErrorMessage(error, 'Failed to upload General Ledger') });
      }
    },
    [setInvalidator, pushInfoToast, navigate, pushErrorToast]
  );
}

export type StatusCallback = (index: number, status: FileUploadStatus, errorMessage?: string) => void;

/** Operator-chosen upload pipeline for the entire batch.
 *  - 'carta': presign → S3 → async loader; date extracted from filename; real
 *    completion polled from the carta status endpoint.
 *  - 'atlas': FA-agnostic Atlas-pipeline endpoint; filename is not used. Each file is
 *    its own delivery (FD-3017 single-source-per-delivery invariant), so per-file
 *    `sourceObject` + optional coverage bounds are supplied via `atlasOptsByIndex`. */
export type UploadRoute = 'carta' | 'atlas';

export function useUploadMultipleGeneralLedgers() {
  const { pushInfoToast, pushErrorToast } = useToastMessageState();
  const navigate = useNavigate();
  const setInvalidator = useSetAtom(invalidateGeneralLedgerData);

  return useCallback(
    async (
      entries: FileUploadEntry[],
      route: UploadRoute,
      atlasOptsByIndex: AtlasUploadOpts[],
      onStatusChange: StatusCallback
    ) => {
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        onStatusChange(i, 'uploading');

        try {
          if (route === 'carta') {
            // Carta endpoint requires a per-file date (extracted from filename).
            // Filename detection still gates this branch — Carta exports always
            // arrive in the canonical `<slug>_YYYY-MM-DD_gl-export.<ext>` shape,
            // and operators uploading Carta files have nothing else to upload.
            if (!entry.detectedDate) {
              throw new Error(
                'Carta upload requires the filename to encode the date. ' +
                  'Expected: <fund-slug>_YYYY-MM-DD_gl-export.{csv,xlsx}.'
              );
            }
            const [upload] = await getCartaGlPresignedUrls([
              { fileName: entry.file.name, period: entry.detectedDate },
            ]);
            if (!upload) {
              throw new Error('Presign returned no upload slot for this file.');
            }
            await uploadToS3(upload.signedUrl, entry.file);
            // Uploaded; the loader now runs asynchronously — reflect that, then poll
            // for the real outcome rather than reporting success at accept time.
            onStatusChange(i, 'processing');
            const result = await pollCartaGlCompletion(upload.cartaGlFileId);
            if (result.status === 'completed') {
              onStatusChange(i, 'success');
              successCount++;
            } else if (result.status === 'error') {
              onStatusChange(i, 'error', result.errorMessage);
              errorCount++;
            } else {
              onStatusChange(i, 'error', CARTA_GL_STILL_PROCESSING);
              errorCount++;
            }
          } else {
            // Atlas pipeline: each file is its own delivery with its own
            // sourceObject and coverage window. Caller supplies one
            // AtlasUploadOpts per row in entries order.
            const formData = new FormData();
            formData.append('file', entry.file);
            const opts = atlasOptsByIndex[i] ?? {};
            await uploadAtlasGeneralLedger(formData, opts);
            onStatusChange(i, 'success');
            successCount++;
          }
        } catch (error) {
          console.error(`Failed to upload ${entry.file.name}:`, error);
          onStatusChange(i, 'error', getErrorMessage(error, 'Upload failed'));
          errorCount++;
        }
      }

      setInvalidator((prev) => prev + 1);

      if (errorCount === 0) {
        pushInfoToast({
          message: `All ${successCount} files processed successfully`,
          buttonLabel: 'Go to Finance',
          buttonAction: () => {
            navigate(`/${ROUTES.FINANCE2}/${ROUTES.FINANCE2_OVERVIEW}`);
          },
        });
      } else {
        pushErrorToast({
          message: `${successCount} of ${entries.length} files processed. ${errorCount} failed.`,
        });
      }
    },
    [setInvalidator, pushInfoToast, pushErrorToast, navigate]
  );
}
